import { useState } from 'react';
import { api } from '../lib/api.js';
import { useMyFlags, flagOn } from '../lib/flags.js';
import Icon from './Icon.jsx';

// "Ask the Owl to build it" — the shared header of the goal/alert editors'
// advanced Build-a-metric section. You describe the number in words and it fills
// the pickers below in: data source, measure, and filters matched to the client's
// REAL values (the server resolves them, so "under 35" becomes the value that
// actually exists). It fills in; the person still reviews and saves — nothing is
// created here, which is why the same component serves both editors.
//
// `onFilled(metric)` hands the parent { model, view, measure, measureLabel,
// filters:[{field,value,options,matched}], event, label } to drop into its own
// state. `onCompare(eventName)` is optional: goals can pace against a past event,
// so they pass a handler and the suggestion becomes a one-tap button; alerts don't.
export default function AskOwlMetric({ suiteId, entityId, inputStyle, onFilled, onCompare, compareBusy = false, compareDone = '', compareErr = '' }) {
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const flags = useMyFlags(entityId || null); // hooks first — the flag gate returns below

  const run = async () => {
    const q = ask.trim();
    if (!q || busy || !suiteId) return;
    setBusy(true); setOut(null);
    try {
      const r = await api.metricAssist(suiteId, q);
      onFilled?.(r.metric || {});
      setOut({
        note: r.note || '',
        compareToEvent: r.compareToEvent || '',
        unmatched: (r.metric?.filters || []).filter((f) => f.matched === 'none').map((f) => `${f.label} = ${f.value}`),
      });
    } catch (e) { setOut({ error: (e && e.message) || 'Couldn’t build that metric — try describing it differently.' }); }
    finally { setBusy(false); }
  };

  if (!flagOn(flags, 'owl.metric_assist')) return null; // the route is gated too; this just hides the box
  const inp = inputStyle || { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 };
  return (
    <div style={{ marginBottom: 10, padding: '9px 10px', border: '1px solid var(--hairline)', borderRadius: 10, background: 'rgba(128,128,128,0.05)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5, display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="owl" size={13} /> Ask the Owl to build it</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={ask} onChange={(e) => setAsk(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); run(); } }}
          placeholder="e.g. under-35 festival tickets, no add-ons — 20% up on last year" style={{ ...inp, flex: '1 1 220px', minWidth: 0 }} />
        <button type="button" onClick={run} disabled={busy || !ask.trim()}
          style={{ ...inp, width: 'auto', flex: '0 0 auto', cursor: busy ? 'default' : 'pointer', fontWeight: 700, color: 'var(--brand)', opacity: busy || !ask.trim() ? 0.55 : 1 }}>
          {busy ? 'Building…' : 'Build it'}
        </button>
      </div>
      {out?.error && <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 6, lineHeight: 1.45 }}>{out.error}</div>}
      {out && !out.error && (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
          Filled in below — check it reads the number you expect.{out.note ? ` ${out.note}` : ''}
          {out.unmatched?.length ? <div style={{ color: '#b45309', marginTop: 3 }}>Couldn’t match {out.unmatched.join(', ')} to a real value — pick it from the dropdown.</div> : null}
          {compareDone ? <div style={{ marginTop: 3 }}>Pace line linked: {compareDone}.</div> : null}
          {compareErr ? <div style={{ color: '#b45309', marginTop: 3 }}>{compareErr}</div> : null}
          {out.compareToEvent && onCompare && !compareDone ? (
            <div style={{ marginTop: 5 }}>
              <button type="button" onClick={() => onCompare(out.compareToEvent)} disabled={compareBusy}
                style={{ border: '1px dashed var(--hairline)', background: 'transparent', color: 'var(--brand)', borderRadius: 9, fontSize: 11.5, fontWeight: 700, padding: '6px 10px', cursor: 'pointer' }}>
                {compareBusy ? 'Computing…' : `Pace it against ${out.compareToEvent}`}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
